import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { AudioRecorder } from '../components/AudioRecorder';
import { BranchPicker } from '../components/ui/BranchPicker';
import { CompleteTaskModal } from '../components/ui/CompleteTaskModal';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { StaffPicker } from '../components/ui/StaffPicker';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useStaffList } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { useDebounced } from '../lib/useDebounced';
import { uploadTaskAudio } from '../lib/upload';
import { Loader } from '../components/ui/Spinner';

type Task = { id: string; taskNumber: string; title: string; description: string | null; assignedTo: string | null; assignedBranchId: string | null; dueAt: string; status: 'open' | 'completed' | 'overdue'; completedAt: string | null };

function AssignForm({ onDone }: { onDone: () => void }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState<'user' | 'branch'>('user');
  const [assignedTo, setAssignedTo] = useState('');
  const [assignedBranchId, setAssignedBranchId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const audioUrl = audioBlob ? await uploadTaskAudio(audioBlob) : undefined;
      return api.post('/api/tasks', { title, description: description || undefined, dueAt: new Date(dueAt).toISOString(), audioUrl, ...(target === 'user' ? { assignedTo } : { assignedBranchId }) });
    },
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['tasks'] }); success('Task assigned'); setAudioBlob(null); onDone(); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not assign task')),
  });

  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
      <label className="label sm:col-span-2">Title<input className="field mt-1" required value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <label className="label sm:col-span-2">Description<textarea className="field mt-1 min-h-20 py-2" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      <label className="label">Assign to<Select className="mt-1" value={target} onChange={(e) => setTarget(e.target.value as 'user' | 'branch')}><option value="user">A staff member</option><option value="branch">Everyone at a branch</option></Select></label>
      {target === 'user'
        ? <label className="label">Staff member<StaffPicker required value={assignedTo} onChange={setAssignedTo} /></label>
        : <label className="label">Branch<BranchPicker required value={assignedBranchId} onChange={setAssignedBranchId} /></label>}
      <label className="label sm:col-span-2">Deadline<input type="datetime-local" step={3600} className="field mt-1" required value={dueAt} onChange={(e) => setDueAt(e.target.value)} /></label>
      <div className="label sm:col-span-2">Voice note<div className="mt-1"><AudioRecorder blob={audioBlob} onChange={setAudioBlob} /></div></div>
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={create.isPending}>Assign task</button>
        <button type="button" className="btn-secondary" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

export function TasksPage() {
  const client = useQueryClient();
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const staff = useStaffList();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [status, setStatus] = useState<'' | 'open' | 'completed'>('open');
  const [assignedTo, setAssignedTo] = useState('');
  const canAssign = user?.roles.some((r) => r === 'branch_head' || r === 'region_head' || r === 'general_head' || r === 'hr' || r === 'admin') ?? false;

  const tasks = useQuery({
    queryKey: ['tasks', { q: debouncedQ, status, assignedTo }],
    queryFn: async () => (await api.get<Task[]>('/api/tasks', { params: { q: debouncedQ || undefined, status: status || undefined, assignedTo: assignedTo || undefined } })).data,
  });
  const [completing, setCompleting] = useState<Task | null>(null);
  const complete = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.post(`/api/tasks/${id}/complete`, { note }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['tasks'] }); success('Task marked complete'); setCompleting(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not complete task')),
  });
  const isOverdue = (task: Task) => task.status === 'open' && new Date(task.dueAt) < new Date();
  const staffName = (id: string | null) => (id ? staff.data?.find((s) => s.id === id)?.name ?? 'Staff' : null);
  const rows = tasks.data ?? [];

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Tasks</h1><p className="mt-1 text-muted">{rows.length} {status || 'matching'} task{rows.length === 1 ? '' : 's'}</p></div>
        {canAssign && <button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> Assign</button>}
      </div>

      {open && <AssignForm onDone={() => setOpen(false)} />}

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <SearchInput placeholder="Search by title or task number…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canAssign && <StaffPicker className="mt-0" clearLabel="Everyone in scope" placeholder="Filter by staff member…" value={assignedTo} onChange={setAssignedTo} />}
      </div>
      <div className="mb-4 flex gap-2">
        {([['open', 'Open'], ['completed', 'Completed'], ['', 'All']] as const).map(([value, label]) => (
          <button key={value || 'all'} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${status === value ? 'bg-brand-600 text-white' : 'border border-border bg-white text-muted'}`} onClick={() => setStatus(value)}>{label}</button>
        ))}
      </div>

      {tasks.isLoading && <Loader />}
      {!tasks.isLoading && rows.length === 0 && <EmptyState icon={CheckCircle2} title="No tasks match these filters" />}

      <div className="space-y-3">
        {rows.map((task) => (
          <article key={task.id} className="card flex items-start justify-between gap-3">
            <Link href={`/tasks/${task.id}`} className="min-w-0 flex-1">
              <span className="font-mono text-xs font-semibold tracking-wide text-muted">{task.taskNumber}</span>
              <strong className={`block ${task.status === 'completed' ? 'text-muted line-through' : ''}`}>{task.title}</strong>
              {task.description && <p className="mt-1 text-sm text-muted">{task.description}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted">
                <span className={`badge ${isOverdue(task) ? 'bg-red-50 text-red-700' : task.status === 'completed' ? 'bg-brand-100 text-brand-700' : ''}`}>{isOverdue(task) && <AlertTriangle size={12} className="mr-1" />}{isOverdue(task) ? 'overdue' : task.status}</span>
                {staffName(task.assignedTo) && <span>{staffName(task.assignedTo)}</span>}
                <span>Due {new Date(task.dueAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
            </Link>
            {task.status !== 'completed' && <button className="btn-secondary shrink-0" onClick={() => setCompleting(task)}><CheckCircle2 size={18} /> Done</button>}
          </article>
        ))}
      </div>
      {completing && (
        <CompleteTaskModal
          taskTitle={completing.title}
          isPending={complete.isPending}
          onConfirm={(note) => complete.mutate({ id: completing.id, note })}
          onClose={() => setCompleting(null)}
        />
      )}
    </>
  );
}
